"use client";

import React, { useCallback, useMemo } from "react";
import { useGlobalModalStack, useUniversalAuth } from "@west-tennessee-consulting/ui";
import { useModalUrlValidation } from "@/lib/modals";
import { useDetailModalData } from "./UniversalDetailModalContainer/hooks/useDetailModalData";
import { useDetailModalActions } from "./UniversalDetailModalContainer/hooks/useDetailModalActions";
import { useDetailModalTabs } from "./UniversalDetailModalContainer/hooks/useDetailModalTabs";
import { useDetailModalHeader } from "./UniversalDetailModalContainer/hooks/useDetailModalHeader";
import { DetailModalContentWithEntityContext } from "./UniversalDetailModalContainer/components/DetailModalContentWithEntityContext";
import { usePublishDetailPageTitle } from "@/lib/hooks/useDocumentTitle";
import type { LinkedCompanyAccount, ParentEntity } from "@/contexts/DetailContextProvider.types";
import {
  computeLinkedCompanyAccountForUniversalDetail,
  computeParentEntityForUniversalDetail,
} from "@/lib/utils/crm/universal-detail-parent-context";
import type {
  BaseEntity,
  UniversalDetailModalContainerProps,
} from "./UniversalCRMTable/types/shared";

// ============================================================================
// MAIN COMPONENT
// ============================================================================

function UniversalDetailModalContainerComponent<T extends BaseEntity>({
  modalId,
  entityName,
  config,
  hooks,
  itemIds,
  currentItemIndex,
}: UniversalDetailModalContainerProps<T>) {
  const { openModal } = useGlobalModalStack();

  // Validate modal URL configuration in development
  useModalUrlValidation(modalId);

  const openEditModal = useCallback(() => {
    openModal(`edit-${entityName}`);
  }, [entityName, openModal]);

  // Get current user for resolving "me" values
  const { appUser } = useUniversalAuth();

  // Data fetching and navigation context
  const { itemId, item, isLoading, error, refetch } = useDetailModalData({
    modalId,
    entityName,
    hooks,
    itemIds,
    currentItemIndex,
  });

  // Action context and handlers
  const { actionContext, handleInlineEdit, handleEdit, handleDelete } = useDetailModalActions({
    item,
    hooks,
    refetch,
    entityName,
    config,
    appUser,
    openEditModal,
  });

  // Tab management
  const { entityTabsConfig, activeTab, setActiveTab } = useDetailModalTabs({
    item,
    entityName,
  });

  // Header and navigation
  const {
    headerTitle,
    headerSubtitle,
    canNavigate,
    canGoPrevious,
    canGoNext,
    handlePrevious,
    handleNext,
  } = useDetailModalHeader({
    item,
    entityName,
    config,
  });

  // Publish the record's name to the browser tab title while the modal is open,
  // matching the full-page detail format ("Libby Lytle · Contact · WTC Hub").
  // The modal only mounts when open, so this lasts exactly the modal's lifetime
  // and clears on close. Null while loading keeps the list route title.
  usePublishDetailPageTitle(
    item ? [headerTitle, config.displayName].filter(Boolean).join(" · ") : null
  );

  // Extract actions from config - prioritize detailActions, fallback to actions if array
  const detailActions = useMemo(() => {
    // First check for detailActions property
    if (config.detailActions && Array.isArray(config.detailActions)) {
      return config.detailActions as any[];
    }
    // Fallback to actions if it's an array (for backward compatibility)
    if (config.actions && Array.isArray(config.actions)) {
      return config.actions as any[];
    }
    // If no actions or TableActions (boolean object), return empty array
    return [];
  }, [config.actions, config.detailActions]);

  const parentEntity = useMemo<ParentEntity | undefined>(
    () => computeParentEntityForUniversalDetail(entityName, item),
    [item, entityName]
  );

  const linkedCompanyAccount = useMemo<LinkedCompanyAccount | null>(
    () => computeLinkedCompanyAccountForUniversalDetail(entityName, item),
    [item, entityName]
  );

  return (
    <DetailModalContentWithEntityContext
      itemId={itemId}
      item={item}
      isLoading={isLoading}
      error={error}
      refetch={refetch}
      modalId={modalId}
      entityName={entityName}
      config={config}
      actionContext={actionContext}
      detailActions={detailActions}
      entityTabsConfig={entityTabsConfig}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      headerTitle={headerTitle}
      headerSubtitle={headerSubtitle}
      canNavigate={canNavigate}
      canGoPrevious={canGoPrevious}
      canGoNext={canGoNext}
      handlePrevious={handlePrevious}
      handleNext={handleNext}
      handleInlineEdit={handleInlineEdit}
      handleEdit={handleEdit}
      handleDelete={handleDelete}
      parentEntity={parentEntity}
      linkedCompanyAccount={linkedCompanyAccount}
    />
  );
}

// ============================================================================
// MEMOIZED EXPORT
// ============================================================================

/**
 * Custom comparison for UniversalDetailModalContainer memoization.
 * Only re-render if the props that affect the modal content actually change.
 */
function areUniversalDetailModalContainerPropsEqual<T extends BaseEntity>(
  prevProps: UniversalDetailModalContainerProps<T>,
  nextProps: UniversalDetailModalContainerProps<T>
): boolean {
  // Re-render if modalId changes
  if (prevProps.modalId !== nextProps.modalId) {
    return false;
  }

  // Re-render if entityName changes
  if (prevProps.entityName !== nextProps.entityName) {
    return false;
  }

  // Re-render if config reference changes
  if (prevProps.config !== nextProps.config) {
    return false;
  }

  // Re-render if hooks reference changes
  if (prevProps.hooks !== nextProps.hooks) {
    return false;
  }

  // Re-render if itemIds array changes
  if (JSON.stringify(prevProps.itemIds) !== JSON.stringify(nextProps.itemIds)) {
    return false;
  }

  // Re-render if currentItemIndex changes
  if (prevProps.currentItemIndex !== nextProps.currentItemIndex) {
    return false;
  }

  // Props are equal, don't re-render
  return true;
}

export const UniversalDetailModalContainer = React.memo(
  UniversalDetailModalContainerComponent,
  areUniversalDetailModalContainerPropsEqual
) as typeof UniversalDetailModalContainerComponent;
